import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, QueryList, ViewChildren, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatFormField, MatHint } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatToolbar } from '@angular/material/toolbar'
import {
  ElderHeaderComponent,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderToastService,
  Filter,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { ProdOrder } from '../../prod-orders/prod-order'
import { WorkItem, WorkItemState } from '../../work-items/work-item'
import { WorkItemTableComponent } from '../../work-items/work-item-table/work-item-table.component'
import { WorkItemTransfer } from '../../work-items/work-item-transfer'
import { WorkItemFilter, WorkItemService } from '../../work-items/work-item.service'
import { WorkOrder, WorkOrderOverview } from '../work-order'
import { WorkOrderFilterBuilder } from '../work-order-filter-builder'
import { WorkOrderHeaderComponent } from '../work-order-header/work-order-header.component'
import { WorkOrderSelectDirective } from '../work-order-select/work-order-select.directive'
import { WorkOrderService } from '../work-order.service'

export interface IWorkOrderItemTransferContext {
  prodOrder: ProdOrder
  orderA: WorkOrder
  orderB: WorkOrder
}

export class TransferTargetContext {
  constructor(
    public order: WorkOrder,
    public overview: WorkOrderOverview
  ) {}

  public get involvedProcessPlanIds(): string[] {
    return this.overview?.aggregate ? Object.keys(this.overview.aggregate.processPlan) : []
  }
}

@Component({
  selector: 'app-work-order-item-transfer',
  templateUrl: './work-order-item-transfer.component.html',
  styleUrls: ['./work-order-item-transfer.component.scss'],
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    ElderHeaderComponent,
    MatFormField,
    MatHint,
    ElderSelectComponent,
    WorkOrderSelectDirective,
    FormsModule,
    ElderSelectValueDirective,
    WorkOrderHeaderComponent,
    WorkItemTableComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkOrderItemTransferComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly orderA$ = new BehaviorSubject<WorkOrder>(null)
  public readonly orderB$ = new BehaviorSubject<WorkOrder>(null)

  public readonly contextA$ = new BehaviorSubject<TransferTargetContext>(null)
  public readonly contextB$ = new BehaviorSubject<TransferTargetContext>(null)

  public readonly orderBSelectFilter$: Observable<Filter[]>

  public selectionA: WorkItem[] = []
  public selectionB: WorkItem[] = []

  @ViewChildren(WorkItemTableComponent)
  public itemTables: QueryList<WorkItemTableComponent>
  private dialogRef = inject<MatDialogRef<WorkOrderItemTransferComponent>>(MatDialogRef)
  private workOrderService = inject(WorkOrderService)
  private workItemService = inject(WorkItemService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const data = inject<IWorkOrderItemTransferContext>(MAT_DIALOG_DATA)
    this.orderA = data.orderA
    this.orderB = data.orderB

    this.orderBSelectFilter$ = this.orderA$.pipe(
      filter((order) => !!order),
      map((order) =>
        WorkOrderFilterBuilder.start()
          .prodOrders(order.prodOrderId)
          .manufactureFacility(order.manufactureFacility)
          .collectingFacility(order.collectingFacility)
          .build()
      )
    )

    this.buildContexts()
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get orderA(): WorkOrder {
    return this.orderA$.getValue()
  }

  public set orderA(value: WorkOrder) {
    if (this.differs(this.orderA, value)) {
      this.orderA$.next(value)
      this.orderB$.next(null)
      this.buildContexts()
    }
  }

  public get orderB(): WorkOrder {
    return this.orderB$.getValue()
  }

  public set orderB(value: WorkOrder) {
    if (this.differs(this.orderB, value)) {
      this.orderB$.next(value)
      this.buildContexts()
    }
  }

  public get contextA(): TransferTargetContext {
    return this.contextA$.getValue()
  }

  public get contextB(): TransferTargetContext {
    return this.contextB$.getValue()
  }

  public get canTransferAtoB() {
    return (
      this.contextA &&
      this.contextB &&
      this.contextA.order.id !== this.contextB.order.id &&
      this.selectionA.length > 0
    )
  }

  public get canTransferBtoA() {
    return (
      this.contextA &&
      this.contextB &&
      this.contextA.order.id !== this.contextB.order.id &&
      this.selectionB.length > 0
    )
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public close(event: Event): void {
    this.dialogRef.close()
  }

  public transferAtoB(): void {
    const transfer = this.createTransfer(this.contextA.order, this.contextB.order, this.selectionA)

    this.requestTransfer(transfer)
  }

  public transferBtoA(): void {
    const transfer = this.createTransfer(this.contextB.order, this.contextA.order, this.selectionB)

    this.requestTransfer(transfer)
  }

  public getSelectableEvaluatorFn(): (w: WorkItem) => boolean {
    return (w: WorkItem) => w.processingState === WorkItemState.OPEN
  }

  public hintResolverFn(workOrder: WorkOrder): string {
    return workOrder ? workOrder.manufactureFacility + '/' + workOrder.collectingFacility : null
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private differs(woA: WorkOrder, woB: WorkOrder): boolean {
    if (woA && woB) {
      return woA.id !== woB.id
    } else {
      return woA !== woB
    }
  }

  private buildContexts(): void {
    if (this.orderA) {
      this.workOrderService.getOverview(this.orderA.id).subscribe((overview) => {
        this.contextA$.next(new TransferTargetContext(this.orderA, overview))
      })
    } else {
      this.contextA$.next(null)
    }

    if (this.orderB) {
      this.workOrderService.getOverview(this.orderB.id).subscribe((overview) => {
        this.contextB$.next(new TransferTargetContext(this.orderB, overview))
      })
    } else {
      this.contextB$.next(null)
    }
  }

  private createTransfer(
    source: WorkOrder,
    target: WorkOrder,
    items: WorkItem[]
  ): WorkItemTransfer {
    const sourceWorkOrderId = source.id
    const targetWorkOrderId = target.id
    const itemIds = items.map((i) => i.id)
    return <WorkItemTransfer>{
      sourceWorkOrderId,
      targetWorkOrderId,
      itemIds,
    }
  }

  private requestTransfer(transfer: WorkItemTransfer): void {
    this.workItemService.transfer(transfer).subscribe({
      next: (_) => {
        this.itemTables.forEach((t) => {
          t.data.reload()
          t.data.filter.updateFilters([WorkItemFilter.processPlans([])])
        })
        this.buildContexts()
      },
      error: (err) => this.toastService.error('Failed to transfer items!', err),
    })
  }
}
